import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { User } from '../types/alarm';
import { checkAlarmCategoryAccess } from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';

interface NewAlarmModalProps {
  isOpen: boolean;
  onSave: (data: { time: string; type: '1M' | '3M' | '5M'; name: string; category?: 'basic' | 'class' }) => void;
  onCancel: () => void;
  timeFormat: '12h' | '24h';
  user?: User;
  onLicenseClick?: () => void;
}

const NewAlarmModal: React.FC<NewAlarmModalProps> = ({ isOpen, onSave, onCancel, timeFormat, user, onLicenseClick }) => {
  const [tempTime, setTempTime] = useState('00:00');
  const [tempType, setTempType] = useState<'1M' | '3M' | '5M'>('1M');
  const [tempName, setTempName] = useState('');
  const [tempCategory, setTempCategory] = useState<'basic' | 'class'>('basic');
  const [isAm, setIsAm] = useState(true);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [hourInput, setHourInput] = useState('');
  const [minuteInput, setMinuteInput] = useState('');
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  // 모달이 열릴 때마다 초기값 설정 및 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      // 배경 스크롤 방지
      document.body.style.overflow = 'hidden';
      
      setTempTime('00:00');
      setTempType('1M');
      setTempName('');
      setTempCategory('basic');
      setIsAm(true);
      setShowTimePicker(false);
      setShowLicenseLimitModal(false);
      setLicenseLimitMessage('');
      setIsCategoryDropdownOpen(false);
      
      // 시간 입력 초기화
      if (timeFormat === '12h') {
        setHourInput('12');
      } else {
        setHourInput('00');
      }
      setMinuteInput('00');
    } else {
      // 모달이 닫힐 때 스크롤 복원
      document.body.style.overflow = 'auto';
    }

    // 컴포넌트가 언마운트될 때 스크롤 복원
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, timeFormat]);

  // 시간 입력 핸들러
  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // 빈 문자열이거나 숫자가 아닌 경우 처리
    if (value === '' || !/^\d*$/.test(value)) {
      setHourInput(value);
      return;
    }
    
    setHourInput(value);
    
    const [, m] = tempTime.split(':').map(Number);
    
    if (timeFormat === '12h') {
      let hour = parseInt(value);
      if (isNaN(hour)) return;
      
      // 12를 넘는 숫자를 입력했을 때만 토글을 오후로 변경
      if (hour > 12) {
        if (isAm) setIsAm(false); // 오후로 변경
      }
      
      // 12시간 형식을 24시간 형식으로 변환
      let newHour = hour;
      if (!isAm && hour < 12) {
        newHour = hour + 12;
      } else if (isAm && hour === 12) {
        newHour = 0;
      }
      
      const newTime = `${newHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      setTempTime(newTime);
    } else {
      // 24시간 형식에서는 입력값이 2자리이고 유효한 시간인 경우에만 tempTime 업데이트
      if (value.length === 2) {
        let hour = parseInt(value);
        if (!isNaN(hour) && hour >= 0 && hour <= 23) {
          const newTime = `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
          setTempTime(newTime);
        }
      }
    }
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // 빈 문자열이거나 숫자가 아닌 경우 처리
    if (value === '' || !/^\d*$/.test(value)) {
      setMinuteInput(value);
      return;
    }
    
    setMinuteInput(value);
    
    // 입력값이 2자리이고 유효한 분인 경우에만 tempTime 업데이트
    if (value.length === 2) {
      const minute = parseInt(value);
      if (!isNaN(minute) && minute >= 0 && minute <= 59) {
        const [h] = tempTime.split(':').map(Number);
        const newTime = `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        setTempTime(newTime);
      }
    }
  };

  // 시간 입력 blur 핸들러
  const handleHourBlur = () => {
    if (timeFormat === '12h') {
      const hour = parseInt(hourInput);
      if (!isNaN(hour) && hour > 12) {
        // 12를 넘는 숫자를 1-12 범위로 변환
        const normalizedHour = hour % 12 || 12;
        setHourInput(normalizedHour.toString());
        
        // 24시간 형식으로 변환하여 tempTime 업데이트
        const [, m] = tempTime.split(':').map(Number);
        let newHour = normalizedHour;
        if (!isAm && normalizedHour < 12) {
          newHour = normalizedHour + 12;
        } else if (isAm && normalizedHour === 12) {
          newHour = 0;
        }
        
        const newTime = `${newHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        setTempTime(newTime);
      }
    } else {
      // 24시간 형식에서 blur 시 포맷팅
      const hour = parseInt(hourInput);
      if (!isNaN(hour) && hour >= 0 && hour <= 23) {
        // 시간을 2자리로 포맷팅
        setHourInput(hour.toString().padStart(2, '0'));
        
        const [, m] = tempTime.split(':').map(Number);
        const newTime = `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        setTempTime(newTime);
      }
    }
  };

  const handleMinuteBlur = () => {
    const minute = parseInt(minuteInput);
    if (!isNaN(minute) && minute >= 0 && minute <= 59) {
      // 분을 2자리로 포맷팅
      setMinuteInput(minute.toString().padStart(2, '0'));
      
      const [h, m] = tempTime.split(':').map(Number);
      const newTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      setTempTime(newTime);
    }
  };

  const handleAmPmToggle = () => {
    let [h, m] = tempTime.split(':').map(Number);
    if (isAm) {
      h += 12;
    } else {
      h -= 12;
    }
    setTempTime(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    setIsAm(!isAm);
  };

  const handleClockClick = () => {
    setShowTimePicker(true);
  };

  const handleTimePickerChange = (type: 'hour' | 'minute', value: string) => {
    let [h, m] = tempTime.split(':').map(Number);
    if (type === 'hour') {
      h = parseInt(value);
      if (!isAm && h < 12) h += 12;
      if (isAm && h >= 12) h -= 12;
    } else {
      m = parseInt(value);
    }
    setTempTime(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  };

  const handleTimePickerClose = () => {
    setShowTimePicker(false);
  };

  const handleSave = () => {
    // 카테고리 접근 권한 체크
    if (user) {
      const accessCheck = checkAlarmCategoryAccess(user, tempCategory);
      if (!accessCheck.allowed) {
        setLicenseLimitMessage(accessCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    onSave({ time: tempTime, type: tempType, name: tempName, category: tempCategory });
  };

  const handleCategoryChange = (category: 'basic' | 'class') => {
    // 카테고리 변경 시 권한 체크
    if (user) {
      const accessCheck = checkAlarmCategoryAccess(user, category);
      if (!accessCheck.allowed) {
        setLicenseLimitMessage(accessCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    setTempCategory(category);
  };

  const getTypeDisplay = (type: '1M' | '3M' | '5M') => {
    switch (type) {
      case '1M': return '1분 전';
      case '3M': return '3분 전';
      case '5M': return '5분 전';
      default: return '1분 전';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onCancel} />
      <ModalWrapper>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>새 알람 만들기</ModalTitle>
        
        <TimeInputContainer>
          <TimeGroup>
            {timeFormat === '12h' && (
              <AmPmToggle isAm={isAm} onClick={handleAmPmToggle}>
                {isAm ? '오전' : '오후'}
              </AmPmToggle>
            )}
            <HourInput
              type="text"
              value={hourInput}
              onChange={handleHourChange}
              onBlur={handleHourBlur}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              placeholder="00"
            />
            <TimeSeparator>:</TimeSeparator>
            <MinuteInput
              type="text"
              value={minuteInput}
              onChange={handleMinuteChange}
              onBlur={handleMinuteBlur}
              onFocus={(e) => e.target.select()}
              maxLength={2}
              placeholder="00"
            />
            <TimeSettingButton onClick={handleClockClick}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M12 6v6l4 2" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </TimeSettingButton>
          </TimeGroup>
        </TimeInputContainer>

        <AlarmNameInput
          type="text"
          value={tempName}
          onChange={(e) => setTempName(e.target.value)}
          placeholder="알람 이름을 입력하세요"
        />

        <CategorySelector>
          <CategoryLabel>알람 종류</CategoryLabel>
          <CategorySelectContainer>
            <CategorySelectButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
              }}
            >
              <CategoryOption>
                {tempCategory === 'basic' ? '기본알람' : '수업알람'}
              </CategoryOption>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CategorySelectButton>
            
            {isCategoryDropdownOpen && (
              <>
                <CategoryDropdownOverlay onClick={() => setIsCategoryDropdownOpen(false)} />
                <CategoryDropdown>
                  <CategoryDropdownOption
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryChange('basic');
                      setIsCategoryDropdownOpen(false);
                    }}
                  >
                    <span>기본알람</span>
                  </CategoryDropdownOption>
                  <CategoryDropdownOption
                    disabled={user?.userType === 'free'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryChange('class');
                      setIsCategoryDropdownOpen(false);
                    }}
                  >
                    <span>수업알람</span>
                  </CategoryDropdownOption>
                </CategoryDropdown>
              </>
            )}
          </CategorySelectContainer>
        </CategorySelector>

        <TypeSelector>
          {(['1M', '3M', '5M'] as const).map(type => (
            <TypeButton
              key={type}
              isSelected={tempType === type}
              onClick={() => setTempType(type)}
              type={type}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px', flexShrink: 0 }}
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {getTypeDisplay(type)}
            </TypeButton>
          ))}
        </TypeSelector>

        <ButtonGroup>
          <CancelButton data-variant="modal-secondary" onClick={onCancel}>취소</CancelButton>
          <SaveButton onClick={handleSave}>저장</SaveButton>
        </ButtonGroup>

        {/* Time Picker Modal */}
        {showTimePicker && (
          <TimePickerModal onClick={handleTimePickerClose}>
            <TimePickerContent onClick={(e) => e.stopPropagation()}>
              <TimePickerTitle>시간 선택</TimePickerTitle>
              <TimePickerRow>
                {timeFormat === '12h' && (
                  <TimePickerColumn>
                    <TimePickerLabel>오전/오후</TimePickerLabel>
                    <TimePickerSelect
                      value={isAm ? 'am' : 'pm'}
                      onChange={(e) => {
                        const newIsAm = e.target.value === 'am';
                        setIsAm(newIsAm);
                        let [h, m] = tempTime.split(':').map(Number);
                        if (newIsAm && h >= 12) {
                          h -= 12;
                        } else if (!newIsAm && h < 12) {
                          h += 12;
                        }
                        setTempTime(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
                      }}
                    >
                      <option value="am">오전</option>
                      <option value="pm">오후</option>
                    </TimePickerSelect>
                  </TimePickerColumn>
                )}
                <TimePickerColumn>
                  <TimePickerLabel>시간</TimePickerLabel>
                  <TimePickerSelect
                    value={timeFormat === '12h' ? (() => {
                      let [h] = tempTime.split(':').map(Number);
                      let hour = h % 12;
                      if (hour === 0) hour = 12;
                      return hour.toString().padStart(2, '0');
                    })() : tempTime.split(':')[0]}
                    onChange={(e) => handleTimePickerChange('hour', e.target.value)}
                  >
                    {Array.from({length: timeFormat === '12h' ? 12 : 24}, (_, i) => {
                      const hour = timeFormat === '12h' ? i + 1 : i;
                      return (
                        <option key={hour} value={hour.toString().padStart(2, '0')}>
                          {hour.toString().padStart(2, '0')}
                        </option>
                      );
                    })}
                  </TimePickerSelect>
                </TimePickerColumn>
                <TimePickerColumn>
                  <TimePickerLabel>분</TimePickerLabel>
                  <TimePickerSelect
                    value={tempTime.split(':')[1]}
                    onChange={(e) => handleTimePickerChange('minute', e.target.value)}
                  >
                    {Array.from({length: 60}, (_, i) => i).map(minute => (
                      <option key={minute} value={minute.toString().padStart(2, '0')}>
                        {minute.toString().padStart(2, '0')}
                      </option>
                    ))}
                  </TimePickerSelect>
                </TimePickerColumn>
              </TimePickerRow>
              <ModalButtonGroup>
                <ModalButton isPrimary onClick={handleTimePickerClose}>
                  확인
                </ModalButton>
              </ModalButtonGroup>
            </TimePickerContent>
          </TimePickerModal>
        )}

        <LicenseLimitModal
          isOpen={showLicenseLimitModal}
          message={licenseLimitMessage}
          onClose={() => setShowLicenseLimitModal(false)}
          onUpgrade={() => {
            setShowLicenseLimitModal(false);
            onCancel();
            onLicenseClick?.();
          }}
        />
      </ModalContent>
      </ModalWrapper>
    </>
  );
};

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 40px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  margin: auto;
`;

const ModalTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: bold;
`;

const TimeInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-surface);
  margin-bottom: 20px;
  position: relative;
  
  &:focus-within {
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const TimeGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0 auto;
`;

const HourInput = styled.input`
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 2px 0;
  border-radius: 8px;
  width: 60px;
  
  &:focus {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #ccc;
  }
`;

const TimeSeparator = styled.span`
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  margin: 0 2px;
`;

const MinuteInput = styled.input`
  font-size: 2.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
  text-align: center;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  padding: 2px 0;
  border-radius: 8px;
  width: 60px;
  
  &:focus {
    background-color: rgba(var(--color-accent-rgb), 0.1);
    outline: none;
  }
  
  &::placeholder {
    color: #ccc;
  }
`;

const AmPmToggle = styled.button<{ isAm: boolean }>`
  background: #bbb;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;
  outline: none;
  white-space: nowrap;
  
  &:focus {
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(73, 80, 87, 0.3);
  }
`;

const TimeSettingButton = styled.button`
  background: transparent;
  color: var(--color-text-gray);
  border: none;
  padding: 6px;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--color-text-primary);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const AlarmNameInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  margin-bottom: 15px;
  box-sizing: border-box;
  text-align: center;
  background: transparent;
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const CategorySelector = styled.div`
  margin-bottom: 20px;
`;

const CategoryLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const CategorySelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CategorySelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const CategoryOption = styled.div`
  flex: 1;
  text-align: center;
`;

const CategoryDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const CategoryDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: hidden;
`;

const CategoryDropdownOption = styled.div<{ disabled?: boolean }>`
  padding: 10px 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;
  font-size: 1rem;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-primary)'};
  text-align: center;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : 'var(--color-panel)'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const TypeSelector = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: space-between;
`;

const TypeButton = styled.button<{ isSelected: boolean; type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: var(--color-surface);
  color: ${props => props.isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-height: 36px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
  font-weight: ${props => props.isSelected ? '600' : '400'};
  
  svg {
    color: ${props => props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
    transition: color 0.2s ease;
  }
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    
    svg {
      color: ${props => props.isSelected ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
    }
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TimePickerModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const TimePickerContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow);
`;

const TimePickerTitle = styled.h3`
  text-align: center;
  margin-bottom: 20px;
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: bold;
`;

const TimePickerRow = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
`;

const TimePickerColumn = styled.div`
  flex: 1;
`;

const TimePickerLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--color-text-muted);
  font-size: 0.8rem;
`;

const TimePickerSelect = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  color: var(--color-text-primary);
  background-color: var(--color-surface);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='lucide lucide-chevron-down'/%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px;

  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ModalButton = styled.button<{ isPrimary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s ease;
  white-space: nowrap;

  ${props => props.isPrimary && `
    background: var(--color-accent-strong);
    color: white;
  `}

  &:hover {
    ${props => props.isPrimary ? 'background: var(--color-accent);' : 'background: var(--color-surface-highlight);'}
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

export default NewAlarmModal;